import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, Lock, LogOut, BarChart3, ArrowRight, BookOpen, Download } from "lucide-react";
import { api, participant } from "../lib/api";
import { Brand } from "../components/Brand";
import { DocText } from "../components/DocText";
import { IMPORTANT_INFO } from "../lib/content";
import { printElement } from "../lib/report";

const PHASES = [
  { key: "baseline", label: "Baseline", sub: "Before departure" },
  { key: "post", label: "Post", sub: "After return" },
];

export default function ParticipantDashboard() {
  const nav = useNavigate();
  const [tests, setTests] = useState([]);
  const [progress, setProgress] = useState(null);
  const [phase, setPhase] = useState("baseline");
  const p = participant.get();

  useEffect(() => {
    if (!p) {
      nav("/");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    const [t, pr] = await Promise.all([
      api.get("/config/tests"),
      api.get(`/participant/${p.code}/progress`),
    ]);
    setTests(t.data.tests);
    setProgress(pr.data);
  };

  if (!progress) return <div className="p-12 text-[#1E293B]/60">Loading…</div>;

  const done = progress[phase] || [];
  const phaseOpen = phase === "baseline" ? progress.baseline_open : progress.post_open;
  const totalDone = done.length;
  const pct = Math.round((totalDone / tests.length) * 100);
  const baselineComplete = (progress.baseline || []).length === tests.length && tests.length > 0;

  const logout = () => {
    participant.clear();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <header className="sticky top-0 z-10 border-b border-[#E2E8F0] bg-[#F1F5F9]/95 px-6 py-4 backdrop-blur sm:px-10">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          <Brand subtitle="Participant" />
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => nav("/activities")} data-testid="book-of-activities-btn"
              className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
              <BookOpen className="h-4 w-4" strokeWidth={1.5} /> Book of Activities
            </button>
            <button onClick={() => nav("/results")} data-testid="view-results-btn"
              className="flex items-center gap-1.5 rounded-full border border-[#1E293B]/20 px-4 py-2 text-sm text-[#1E293B] transition-transform duration-200 hover:-translate-y-0.5">
              <BarChart3 className="h-4 w-4" strokeWidth={1.5} /> My Results
            </button>
            <button onClick={logout} data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm text-[#1E293B]/60 transition-colors hover:text-[#2563EB]">
              <LogOut className="h-4 w-4" strokeWidth={1.5} /> Exit
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 sm:px-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB]">Welcome back</div>
          <h1 className="mt-2 font-display text-4xl text-[#1E293B]">{progress.name}</h1>
          <p className="mt-2 text-[#1E293B]/60">
            Complete all eight sections twice — once as a <strong>baseline</strong> and again <strong>after</strong> your experience.
          </p>
        </motion.div>

        {/* phase switch */}
        <div className="mt-8 flex gap-2 rounded-full border border-[#E2E8F0] bg-white p-1" style={{ width: "fit-content" }}>
          {PHASES.map((ph) => (
            <button
              key={ph.key}
              onClick={() => setPhase(ph.key)}
              data-testid={`phase-tab-${ph.key}`}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                phase === ph.key ? "bg-[#1E293B] text-white" : "text-[#1E293B]/60 hover:text-[#1E293B]"
              }`}
            >
              {ph.label} <span className="hidden sm:inline text-xs opacity-70">· {ph.sub}</span>
            </button>
          ))}
        </div>

        {phase === "baseline" && baselineComplete && (
          <div id="important-info" className="mt-6 rounded-xl border border-[#2563EB]/30 bg-white p-6" data-testid="important-info">
            <div className="no-print mb-3 flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2563EB]">Pre-departure complete</span>
              <button onClick={() => printElement("#important-info")} data-testid="download-info-btn"
                className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                <Download className="h-4 w-4" strokeWidth={1.5} /> Download PDF
              </button>
            </div>
            <DocText text={IMPORTANT_INFO} />
          </div>
        )}

        {/* progress bar */}
        {phaseOpen && (
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm text-[#1E293B]/60">
              <span>{totalDone} of {tests.length} completed</span>
              <span>{pct}%</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#E2E8F0]">
              <div className="h-full rounded-full bg-[#2563EB] transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        {!phaseOpen ? (
          <div className="mt-6 rounded-xl border border-[#2563EB]/30 bg-[#2563EB]/5 p-6 text-sm text-[#1E293B]" data-testid="phase-closed-notice">
            <Lock className="mr-2 inline h-4 w-4" strokeWidth={1.5} />
            The <strong>{phase}</strong> window is not open right now. Your administrator controls the available dates.
            {(() => {
              const s = progress.settings || {};
              const start = s[`${phase}_start`];
              const end = s[`${phase}_end`];
              if (!start && !end) return null;
              return (
                <div className="mt-3 text-[#1E293B]/70" data-testid="phase-dates">
                  {start && <>Opens <strong>{start}</strong>. </>}
                  {end && <>Closes <strong>{end}</strong>.</>}
                </div>
              );
            })()}
            <div className="mt-2 text-[#1E293B]/60">Please come back on the scheduled date to continue.</div>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {tests.map((t, i) => {
              const isDone = done.includes(t.id);
              if (isDone) {
                return (
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    data-testid={`section-card-${t.id}`}
                    className="flex items-start justify-between rounded-xl border border-[#15803D]/30 bg-[#15803D]/5 p-6 text-left"
                  >
                    <div className="pr-4">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#1E293B]/40">
                        Section {t.section} · {t.code}
                      </div>
                      <div className="mt-1 font-display text-xl text-[#1E293B]">{t.title}</div>
                      <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-[#15803D]">
                        <CheckCircle2 className="h-4 w-4" /> Completed · locked
                      </div>
                    </div>
                    <CheckCircle2 className="h-6 w-6 shrink-0 text-[#15803D]" />
                  </motion.div>
                );
              }
              return (
                <motion.button
                  key={t.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => nav(`/test/${t.id}/${phase}`)}
                  data-testid={`section-card-${t.id}`}
                  className="group flex items-start justify-between rounded-xl border border-[#E2E8F0] bg-white p-6 text-left transition-transform duration-200 ease-out hover:-translate-y-1"
                >
                  <div className="pr-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#1E293B]/40">
                      Section {t.section} · {t.code}
                    </div>
                    <div className="mt-1 font-display text-xl text-[#1E293B]">{t.title}</div>
                    <div className="mt-1 text-sm text-[#1E293B]/60">{t.questions.length} questions</div>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-[#2563EB] transition-transform group-hover:translate-x-1" />
                </motion.button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
